<template>
    <div>
    <label v-if="fixedLabel" class="settings-field-label" :for="`search-stock-${_uid}`">{{ label || $translate('select_stock') }}</label>
    <SearchObject
        :input-id="fixedLabel ? `search-stock-${_uid}` : undefined"
        :hide-label="fixedLabel"
        :placeholder="fixedLabel ? label : undefined"
        class="mt-0"
        :load-on-mount="loadOnMount"
        :additional-params="{ ordering: 'name', ...additionalParams }"
        :allow-create="allowCreate"
        :create-label="createLabel || $translate('create_a_warehouse')"
        :autofocus="autofocus"
        :base-url="baseUrl"
        :disabled="disabled"
        :hide-details="hideDetails"
        :item-text="itemText"
        :label="label || $translate('select_stock')"
        :readonly="readonly"
        :required="required"
        :value="value"
        :select-mode="selectWithoutSearch"
        @create="createWarehouse"
        @input="onSearchInput"
        @select="$emit('select', $event)"
        :error-messages="errorMessages"
        ref="search"
        :dense="dense"
        :clearable="clearable"
        :flat="flat"
        :outlined="outlined"
        :icon="icon"
        :menu-props="stockListMenuProps"
    >
        <template slot="item" slot-scope="{ item }">
            <StockOptionLabel
                variant="menu"
                :name="item.name || ''"
                :owner-name="stockItemOwnerCaption(item)"
            />
        </template>
        <template v-if="selectWithoutSearch" slot="selection" slot-scope="{ item }">
            <StockOptionLabel
                v-if="item"
                variant="field"
                :name="item.name || ''"
                :owner-name="stockItemOwnerCaption(item)"
            />
        </template>
    </SearchObject>
    </div>
</template>

<script>
import GlobalMixins from "@/mixins/global";
import StockOptionLabel from "@/components/common/stock-option-label.vue";
import StockEdit from "@/modals/stock.vue";

export default {
    mixins: [GlobalMixins],
    name: "SearchStock",
    components: {
        StockOptionLabel,
    },
    props: {
        fixedLabel: Boolean,
        // The base URL to fetch items from.
        baseUrl: {
            type: String,
            default: "/api/stocks/",
        },
        // Additional GET parameters to include in the API request.
        additionalParams: {
            type: Object,
            default: () => ({}),
        },
        // Whether the component is disabled.
        disabled: {
            type: Boolean,
            default: false,
        },
        // v-model binding (selected item ID).
        value: {
            type: [String, Number],
            default: null,
        },
        // Label for the autocomplete.
        label: {
            type: String,
            default: "",
        },
        // Optional limit per request.
        limit: {
            type: Number,
            default: 20,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        createLabel: {
            type: String,
            default: "",
        },
        /** Passed to the stock modal when using the create flow (e.g. preset establishment). */
        createModalParams: {
            type: Object,
            default: () => ({}),
        },
        hideDetails: {
            type: [Boolean, String],
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        errorMessages: {
            type: [String, Array],
            default: null,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        flat: {
            type: Boolean,
            default: false,
        },
        loadOnMount: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, use a plain v-select (no search field) and show owner under the name
         * in the closed field via StockOptionLabel.
         */
        selectWithoutSearch: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: null,
        },
    },
    computed: {
        stockListMenuProps() {
            return { contentClass: "stock-option-label-v-menu" };
        },
    },
    methods: {
        async createWarehouse() {
            const result = await this.showModal({
                component: StockEdit,
                params: { ...this.createModalParams },
            });
            if (result) {
                this.onSearchInput(result.id || result);
                this.$refs.search.update();
            } else {
                this.$refs.search.reset();
            }
        },
        onSearchInput(val) {
            if (val == null || val === "") {
                this.$emit("input", null);
                return;
            }
            const id = typeof val === "object" && val !== null ? val.id : val;
            this.$emit("input", id);
        },
        stockItemOwnerCaption(item) {
            if (!item) {
                return "";
            }
            const captions = [];
            const establishmentName = item.establishment_data?.name;
            if (establishmentName && establishmentName !== item.name) {
                captions.push(establishmentName);
            }
            if (!item.owned) {
                const owner = (item.owner_data && item.owner_data.name) || "";
                if (owner) {
                    captions.push(owner);
                }
            }
            return captions.join(" · ");
        },
        itemText(item) {
            if (!item) {
                return "";
            }
            if (this.selectWithoutSearch) {
                return item.name || "";
            }
            if (item.owned) {
                return item.name || "";
            }
            const owner = (item.owner_data && item.owner_data.name) || "";
            return owner ? `${item.name} (${owner})` : item.name || "";
        },
        focus() {
            this.$refs.search.focus();
        },
    },
    i18n: {
        messages: {
            en: {
                select_stock: "Select a warehouse",
                create_a_warehouse: "Create a warehouse",
            },
            fr: {
                select_stock: "Sélectionnez un entrepôt",
                create_a_warehouse: "Créer un entrepôt",
            },
        },
    },
};
</script>

